import {
    Box,
    Button,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    InputAdornment,
    makeStyles,
    TextField,
    Typography,
} from "@material-ui/core";
import {
    ToggleButton,
    ToggleButtonGroup
} from "@material-ui/lab";
import { KeyboardDatePicker } from "@material-ui/pickers";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
    checkExtIsImg,
    CustomCheckbox,
    modal
} from "../../../util";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const useStyles = makeStyles((theme) => ({
    innerBox: {
        background: "#ffffff",
        marginBottom: theme.spacing(4),
        "& *": {
            fontSize: "13px",
            lineHeight: "15px",
        },
    },
    sectionBox: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    sectionTitle: {
        fontWeight: "300",
        fontSize: "13px",
        lineHeight: "15px",
        color: "#333333",
    },
    itemTitle: {
        fontWeight: "bold",
        fontSize: "12px",
        lineHeight: "14px",
        color: "#333333",
    },
    itemValue: {
        fontWeight: "normal",
        fontSize: "14px",
        lineHeight: "16px",
        color: "#333333",
    },
    input: {
        width: "100%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
            width: "25%",
        },
    },
    buttonGroup: {
        textAlign: "right",
        marginTop: "-20px",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    customLabel: {
        "& label": {
            color: "black",
            fontWeight: 700,
            fontSize: "16px",
        },
    },
    select: {
        "& .MuiSelect-icon": {
            top: "calc(50% - 6px)"
        }
    },
    checkbox: {
        "& .MuiSvgIcon-root": {
            fontSize: "22px"
        }
    },
    formLabel: {
        width: "fit-content"
    },
    others: {
        paddingLeft: "0 !important"
    },
    errors: {
        fontSize: "14px",
        color: "red",
        marginTop: "15px"
    }
}));

const defaultFormValues = {
    title: "",
    surname: "",
    givenName: "",
    mailingAddress: "",
    country: "",
    city: "",
    telephone: "",
    officeTelephone: "",
    fax: "",
    passcode: "",
    email: "",
    position: {
        "Reporter": false,
        "Editor": false,
        "Photographer": false,
        "Videographer": false,
        "Film Critic": false,
        "Producer": false,
        "Others": false,
    },
    positionOthers: "",
    medium: {
        "Newspaper": false,
        "Magazine": false,
        "Radio": false,
        "Website": false,
        "Mobile App": false,
        "News Agency": false,
        "TV": false,
        "Trade Publications": false,
        "We Media/Citizen Media": false,
        "Others": false,
    },
    mediumOthers: "",
    periodicity: {
        "Daily": false,
        "Weekly": false,
        "Bi-Weekly": false,
        "Monthly": false,
        "Bi-Monthly": false,
        "Quarterly": false,
        "Others": false
    },
    periodicityOthers: "",
    nameOfPublication: "",
    countryMedia: "",
    regionalBranch: "",
    officialWebsite: "",
    certifyingLetter: "",
    workingPass: "",
    personalPhoto: "",
    businessCard: "",
    newsClippingsPath: {
        "0": "",
        "1": ""
    },
    newsLang: {
        "English": false,
        "Traditional Chinese": false,
        "Simplified Chinese": false,
    },
    applicationType: {
        "hkiff": false,
        "haf": false,
    },
    durationFrom: "",
    durationTo: "",
    contactAddress: "",
    contactTelephone: "",
    contactFax: "",
    event: "",
    status: "",
    hafStatus: "",
};

const A = styled.a`
  color: #306F9D;
  text-decoration: none;
`;

function MediaAccreditationDetailPage({
    disabled,
    ...props
}) {
    const classes = useStyles();
    const { match } = props;
    const { id } = match.params;
    const [defaultValue, setDefaultValue] = React.useState(defaultFormValues);
    const [emailType, setEmailType] = React.useState('');
    const [hafType, setHafType] = React.useState(false);
    const [hafDisabled, setHafDisabled] = React.useState(false)
    const [HkiffDisabled, setHkiffDisabled] = React.useState(false)
    const { setLoading } = useLoading();

    Yup.addMethod(Yup.object, "atLeastOneChecked", function (errorMessage) {
        return this.test(`test-one-checked`, errorMessage, function (value) {
            const { path, createError } = this;
            const condition = Object.values(value).every(v => !v);
            console.log('condition is evaluated to ', condition);
            return (
                condition ?
                    createError({
                        path,
                        message: errorMessage
                    }) :
                    true
            );
        });
    });

    const validationSchema = Yup.object().shape({
        position: Yup.object().atLeastOneChecked("Choose at least one position"),
        positionOthers: Yup.string().when('position.Others', (others, schema) => {
            return schema.test({
                test: positionOthers => !others || !!positionOthers,
                message: 'Required'
            })
        }),
        medium: Yup.object().atLeastOneChecked("Choose at least one medium"),
        mediumOthers: Yup.string().when('medium.Others', (others, schema) => {
            return schema.test({
                test: mediumOthers => !others || !!mediumOthers,
                message: 'Required'
            })
        }),
        // periodicity: Yup.object().atLeastOneChecked("Choose at least one periodicity"),
        periodicityOthers: Yup.string().when('periodicity.Others', (others, schema) => {
            return schema.test({
                test: periodicityOthers => !others || !!periodicityOthers,
                message: 'Required'
            })
        }),
        newsLang: Yup.object().atLeastOneChecked("Choose at least one news language"),
        applicationType: Yup.object().atLeastOneChecked("Choose at least one application type"),
        passcode: Yup.string().test('passcode-format', 'Passcode must be exactly 6 digits', function(value) {
            if (!value) return true; // Allow empty
            return /^[A-Za-z0-9]{6}$/.test(value);
        }),
    });

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        getValues,
        reset,
        trigger,
        formState: { errors }
    } = useForm({
        defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
        resolver: yupResolver(validationSchema),
    });

    const certifyingLetter = watch("certifyingLetter");
    const workingPass = watch("workingPass");
    const personalPhoto = watch("personalPhoto");
    const businessCard = watch("businessCard");
    const newsClippingsPath = watch("newsClippingsPath");
    const position = watch('position');
    const medium = watch('medium');
    const periodicity = watch('periodicity');

    const checkedValuesToString = (obj) => {
        let string = "";
        Object.keys(obj).forEach((key) => {
            if (obj[key]) {
                string += `${key},`;
            }
        });
        if (!!string.length) {
            string = string.substring(0, string.length - 1);
        }
        return string;
    }

    const onSubmit = async (value) => {
        setLoading(true);
        try {
            const processedValue = JSON.stringify({
                ...value,
                newsClippingsPath: JSON.stringify(value.newsClippingsPath),
                position: checkedValuesToString(value.position),
                positionOthers: position["Others"] ? value.positionOthers : '',
                medium: checkedValuesToString(value.medium),
                mediumOthers: medium["Others"] ? value.mediumOthers : '',
                periodicity: checkedValuesToString(value.periodicity),
                periodicityOthers: periodicity["Others"] ? value.periodicityOthers : '',
                newsLang: checkedValuesToString(value.newsLang),
                applicationType: checkedValuesToString(value.applicationType),
            });
            const result = await apiHelper.hkiff.updateMediaById(id, processedValue);
            if (result.status === 200) {
                toast.dark("Media accreditation is updated.");
                getMediaById();
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    };

    const sendMediaEmail = async (type) => {
        setLoading(true);
        try {
            await apiHelper.hkiff.sendMediaEmailById(id, type);
            toast.dark("Your email is sent.");
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    }

    const sendPasscodeEmail = async () => {
        // Validate all form fields before proceeding to prevent saving invalid data
        const isFormValid = await trigger();
        if (!isFormValid) {
            toast.error("Please fix all validation errors before sending the email.");
            return;
        }

        setLoading(true);
        try {
            // First update the form data
            const currentValues = getValues();
            const processedValue = JSON.stringify({
                ...currentValues,
                newsClippingsPath: JSON.stringify(currentValues.newsClippingsPath),
                position: checkedValuesToString(currentValues.position),
                positionOthers: position["Others"] ? currentValues.positionOthers : '',
                medium: checkedValuesToString(currentValues.medium),
                mediumOthers: medium["Others"] ? currentValues.mediumOthers : '',
                periodicity: checkedValuesToString(currentValues.periodicity),
                periodicityOthers: periodicity["Others"] ? currentValues.periodicityOthers : '',
                newsLang: checkedValuesToString(currentValues.newsLang),
                applicationType: checkedValuesToString(currentValues.applicationType),
            });
            
            const updateResult = await apiHelper.hkiff.updateMediaById(id, processedValue);
            if (updateResult.status === 200) {
                // Then send the passcode email using the Media Accreditation unified endpoint
                await apiHelper.hkiff.sendMediaEmailById(id, 'm_passcode');
                toast.dark("Passcode email is sent.");
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    }

    const handleFileUpload = (field, e) => {
        apiHelper.hkiff
            .uploadHKIFFFile(e.target.files[0], "MEDIA_ACCREDITATION")
            .then((response) => {
                console.log(response);
                setValue(field, response.data.url);
            })
            .catch((error) => console.log(error));
    };

    const getMediaById = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getMediaById(id);
            // handling newsClippingsPath
            let processedNewsClippingsPath;
            try {
                processedNewsClippingsPath = JSON.parse(result.data.newsClippingsPath);
            } catch (e) {
                console.log(e);
            }
            // handling position
            const positionArray = result.data.position.split(' ').join('').split(',');
            const processedPosition = { ...defaultFormValues.position }
            Object.keys(processedPosition).forEach(key => {
                if (positionArray.includes(key)) {
                    processedPosition[key] = true;
                }
            })
            if (result.data.positionOthers) {
                processedPosition["Others"] = true;
            }
            // handling medium
            const mediumArray = result.data.medium.split(',');
            const processedMedium = { ...defaultFormValues.medium }
            Object.keys(processedMedium).forEach(key => {
                if (mediumArray.includes(key)) {
                    processedMedium[key] = true;
                }
            })
            if (result.data.mediumOthers) {
                processedMedium["Others"] = true;
            }
            // handling periodicity
            const periodicityArray = result.data.periodicity ? result.data.periodicity.split(',') : [];
            const processedPeriodicity = { ...defaultFormValues.periodicity }
            Object.keys(processedPeriodicity).forEach(key => {
                if (periodicityArray.includes(key)) {
                    processedPeriodicity[key] = true;
                }
            })
            if (result.data.periodicityOthers) {
                processedPeriodicity["Others"] = true;
            } else {
                result.data.periodicityOthers = "";
            }
            // handling newsLang
            const newsLangArray = result.data.newsLang.split(',');
            const processedNewsLang = { ...defaultFormValues.newsLang }
            Object.keys(processedNewsLang).forEach(key => {
                if (newsLangArray.includes(key)) {
                    processedNewsLang[key] = true;
                }
            })
            // handling applicationType
            const applicationTypeArray = result.data.applicationType ? result.data.applicationType.split(',') : [];
            const processedApplicationType = { ...defaultFormValues.applicationType }
            Object.keys(processedApplicationType).forEach(key => {
                if (applicationTypeArray.includes(key)) {
                    processedApplicationType[key] = true;
                }
            })
            let type = '';
            let haf_type = '';
            switch (result.data.status) {
                default:
                case 0:
                case 3:
                    break;
                case 1:
                    type = 'm_a_confirm';
                    break;
                case 2:
                    type = 'm_a_reject';
            }
            switch (result.data.hafStatus) {
                default:
                case 0:
                case 3:
                    break;
                case 1:
                    haf_type = 'm_a_haf_confirm'
                    break;
                case 2:
                    haf_type = 'm_a_haf_reject'
            }
            setEmailType(type);
            setHafType(haf_type);

            setHafDisabled(!processedApplicationType.haf)
            setHkiffDisabled(!processedApplicationType.hkiff)

            const processedValue = {
                ...result.data,
                newsClippingsPath: processedNewsClippingsPath,
                position: processedPosition,
                medium: processedMedium,
                periodicity: processedPeriodicity,
                newsLang: processedNewsLang,
                applicationType: processedApplicationType
            };
            setDefaultValue(processedValue);
            reset(processedValue);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    React.useEffect(() => {
        getMediaById();
    }, [getMediaById]);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Accreditation &gt; Update Media
                Accreditation
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled}
                    className="fieldset">
                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Name
                                        Title</strong>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid container item
                                    direction="column"
                                    spacing={1} xs={6}>
                                    <Grid item>
                                        <Controller
                                            control={control}
                                            name="title"
                                            render={({
                                                field: {
                                                    value,
                                                    onChange,
                                                    ...rest
                                                },
                                                fieldState,
                                            }) => (
                                                <ToggleButtonGroup
                                                    {...rest}
                                                    value={value}
                                                    onChange={(event, newFormats) => {
                                                        if (newFormats !== null) {
                                                            onChange(newFormats);
                                                        }
                                                    }}
                                                    exclusive
                                                    aria-label="Name Title"
                                                    className={classes.input}
                                                >
                                                    <ToggleButton
                                                        value={0}
                                                        aria-label="Mr">
                                                        Mr
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={2}
                                                        aria-label="Ms">
                                                        Ms
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={3}
                                                        aria-label="Mrs">
                                                        Mrs
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={4}
                                                        aria-label="Dr">
                                                        Dr
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={5}
                                                        aria-label="Miss">
                                                        Miss
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box className={classes.sectionBox}>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="surname"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Surname"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name="givenName"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Given Name"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={12}>
                                    <Controller
                                        name="mailingAddress"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Mailing Address"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Country"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name="city"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="City"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="telephone"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Telephone"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name="officeTelephone"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Office Telephone"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="fax"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Fax"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Email"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="passcode"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Passcode"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Application
                                        Type</strong>
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        {Object.keys(defaultValue.applicationType).map((key) => (
                                            <Controller
                                                name={`applicationType.${key}`}
                                                control={control}
                                                defaultValue={defaultValue.applicationType[key]}
                                                render={({
                                                    field: {
                                                        value,
                                                        onChange,
                                                        ...rest
                                                    },
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <FormControlLabel
                                                            control={
                                                                <CustomCheckbox
                                                                    color="primary"
                                                                    checked={value}
                                                                    onChange={(event) => {
                                                                        const newChecked = event.target.checked;
                                                                        onChange(newChecked);
                                                                        if (key === 'haf') {
                                                                            setHafDisabled(!event.target.checked)
                                                                            newChecked ? setValue('hafStatus', 3) : setValue('hafStatus', 0)
                                                                        }
                                                                        if (key == 'hkiff') {
                                                                            setHkiffDisabled(!event.target.checked)
                                                                            newChecked ? setValue('status', 3) : setValue('status', 0)
                                                                        }
                                                                        errors.applicationType && delete errors.applicationType;
                                                                    }}
                                                                    className={classes.checkbox}
                                                                    {...rest}
                                                                    style={errors.applicationType && { color: "red" }}
                                                                />
                                                            }
                                                            className={classes.formLabel}
                                                            label={key}
                                                        />
                                                    )
                                                }
                                                }
                                            />
                                        ))}
                                        <FormHelperText
                                            className={classes.errors}>
                                            {
                                                errors.applicationType && errors.applicationType.message
                                            }
                                        </FormHelperText>
                                    </FormGroup>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Position</strong>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        {Object.keys(defaultValue.position).map((key) => (
                                            <Controller
                                                name={`position.${key}`}
                                                control={control}
                                                defaultValue={defaultValue.position[key]}
                                                render={({
                                                    field: {
                                                        value,
                                                        onChange,
                                                        ...rest
                                                    },
                                                }) => {
                                                    return (
                                                        <FormControlLabel
                                                            control={
                                                                <CustomCheckbox
                                                                    color="primary"
                                                                    checked={value}
                                                                    onChange={(event) => {
                                                                        onChange(event.target.checked);
                                                                        errors.position && delete errors.position;
                                                                    }}
                                                                    className={classes.checkbox}
                                                                    {...rest}
                                                                    style={errors.position && { color: "red" }}
                                                                />
                                                            }
                                                            className={classes.formLabel}
                                                            label={key}
                                                        />
                                                    )
                                                }
                                                }
                                            />
                                        ))}
                                        <FormHelperText
                                            className={classes.errors}>
                                            {
                                                errors.position && errors.position.message
                                            }
                                        </FormHelperText>
                                    </FormGroup>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={8}
                                    className={classes.others}>
                                    {position["Others"] && (
                                        <Controller
                                            name="positionOthers"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    label="Please enter position"
                                                    variant="outlined"
                                                    margin="dense"
                                                    className={classes.textInput}
                                                    fullWidth
                                                    {...field}
                                                    error={!!error}
                                                    helperText={error ? error.message : ""}
                                                />
                                            )}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Medium</strong>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        {Object.keys(defaultValue.medium).map((key) => (
                                            <Controller
                                                name={`medium.${key}`}
                                                control={control}
                                                defaultValue={defaultValue.medium[key]}
                                                render={({
                                                    field: {
                                                        value,
                                                        onChange,
                                                        ...rest
                                                    },
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <FormControlLabel
                                                            control={
                                                                <CustomCheckbox
                                                                    color="primary"
                                                                    checked={value}
                                                                    onChange={(event) => {
                                                                        onChange(event.target.checked);
                                                                        errors.medium && delete errors.medium;
                                                                    }}
                                                                    className={classes.checkbox}
                                                                    {...rest}
                                                                    style={errors.medium && { color: "red" }}
                                                                />
                                                            }
                                                            className={classes.formLabel}
                                                            label={key}
                                                        />
                                                    )
                                                }
                                                }
                                            />
                                        ))}
                                        <FormHelperText
                                            className={classes.errors}>
                                            {
                                                errors.medium && errors.medium.message
                                            }
                                        </FormHelperText>
                                    </FormGroup>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={8}
                                    className={classes.others}>
                                    {medium["Others"] && (
                                        <Controller
                                            name="mediumOthers"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    label="Please enter medium"
                                                    variant="outlined"
                                                    margin="dense"
                                                    className={classes.textInput}
                                                    fullWidth
                                                    {...field}
                                                    error={!!error}
                                                    helperText={error ? error.message : ""}
                                                />
                                            )}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Periodicity</strong>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        {Object.keys(defaultValue.periodicity).map((key) => (
                                            <Controller
                                                name={`periodicity.${key}`}
                                                control={control}
                                                defaultValue={defaultValue.periodicity[key]}
                                                render={({
                                                    field: {
                                                        value,
                                                        onChange,
                                                        ...rest
                                                    },
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <FormControlLabel
                                                            control={
                                                                <CustomCheckbox
                                                                    color="primary"
                                                                    checked={value}
                                                                    onChange={(event) => {
                                                                        onChange(event.target.checked);
                                                                        errors.periodicity && delete errors.periodicity;
                                                                    }}
                                                                    className={classes.checkbox}
                                                                    {...rest}
                                                                    style={errors.medium && { color: "red" }}
                                                                />
                                                            }
                                                            className={classes.formLabel}
                                                            label={key}
                                                        />
                                                    )
                                                }
                                                }
                                            />
                                        ))}
                                        <FormHelperText
                                            className={classes.errors}>
                                            {
                                                errors.periodicity && errors.periodicity.message
                                            }
                                        </FormHelperText>
                                    </FormGroup>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={8}
                                    className={classes.others}>
                                    {periodicity["Others"] && (
                                        <Controller
                                            name="periodicityOthers"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    label="Please enter periodicity"
                                                    variant="outlined"
                                                    margin="dense"
                                                    className={classes.textInput}
                                                    fullWidth
                                                    {...field}
                                                    error={!!error}
                                                    helperText={error ? error.message : ""}
                                                />
                                            )}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Publication
                                        Details</strong>
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="nameOfPublication"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Name Of Publication"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name="countryMedia"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Country Media"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <Controller
                                        name="regionalBranch"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Regional Branch"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name="officialWebsite"
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error }
                                        }) => (
                                            <TextField
                                                label="Official Website"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <TextField
                                        htmlFor={`raised-button-file-certifying-letter`}
                                        label="Upload Certifying Letter"
                                        className={classes.fileInput}
                                        value=""
                                        variant="outlined"
                                        placeholder="Upload Certifying Letter"
                                        fullWidth
                                        InputProps={{
                                            classes: {
                                                input: classes["&::placeholder"],
                                            },
                                            endAdornment: (
                                                <Controller
                                                    control={control}
                                                    name="certifyingLetter"
                                                    render={({ field }) => {
                                                        return (
                                                            <InputAdornment
                                                                position="end">
                                                                <input
                                                                    style={{ display: "none" }}
                                                                    id={`raised-button-file-certifying-letter`}
                                                                    type="file"
                                                                    onChange={(e) => {
                                                                        handleFileUpload("certifyingLetter", e);
                                                                    }}
                                                                />
                                                                <Button
                                                                    htmlFor={`raised-button-file-certifying-letter`}
                                                                    variant="outlined"
                                                                    component="label"
                                                                    size="small"
                                                                >
                                                                    Choose
                                                                </Button>
                                                            </InputAdornment>
                                                        );
                                                    }}
                                                />
                                            ),
                                        }}
                                    />

                                    {certifyingLetter && (
                                        <p>
                                            {checkExtIsImg(certifyingLetter) && (
                                                <>
                                                    <img
                                                        src={certifyingLetter}
                                                        style={{
                                                            maxHeight: "30px",
                                                            maxWidth: "30px"
                                                        }}
                                                        onClick={() => window.open(certifyingLetter, '_blank').focus()}
                                                        alt=""
                                                    />
                                                    &nbsp;
                                                </>
                                            )
                                            }
                                            <A href={certifyingLetter}
                                                target="_blank"><small>{certifyingLetter.split("/").pop()}</small></A>
                                            <small
                                                style={{
                                                    marginLeft: "20px",
                                                    textDecoration: "underline",
                                                    color: "#962013",
                                                    cursor: "pointer",
                                                }}
                                                onClick={async () => {
                                                    const decision = await modal.fire({
                                                        title: "Delete File",
                                                        text: "Are you sure to delete this file?",
                                                        showCancelButton: true,
                                                        confirmButtonText: `Delete`,
                                                    });
                                                    if (decision.isConfirmed) {
                                                        setValue("certifyingLetter", "");
                                                    }
                                                }}
                                            >
                                                Delete
                                            </small>
                                        </p>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <TextField
                                        htmlFor={`raised-button-file-working-pass`}
                                        label="Upload Working Pass"
                                        className={classes.fileInput}
                                        value=""
                                        variant="outlined"
                                        placeholder="Upload Working Pass"
                                        fullWidth
                                        InputProps={{
                                            classes: {
                                                input: classes["&::placeholder"],
                                            },
                                            endAdornment: (
                                                <Controller
                                                    control={control}
                                                    name="workingPass"
                                                    render={({ field }) => {
                                                        return (
                                                            <InputAdornment
                                                                position="end">
                                                                <input
                                                                    style={{ display: "none" }}
                                                                    id={`raised-button-file-working-pass`}
                                                                    type="file"
                                                                    onChange={(e) => {
                                                                        handleFileUpload("workingPass", e);
                                                                    }}
                                                                />
                                                                <Button
                                                                    htmlFor={`raised-button-file-working-pass`}
                                                                    variant="outlined"
                                                                    component="label"
                                                                    size="small"
                                                                >
                                                                    Choose
                                                                </Button>
                                                            </InputAdornment>
                                                        );
                                                    }}
                                                />
                                            ),
                                        }}
                                    />

                                    {workingPass && (
                                        <p>
                                            <img
                                                src={workingPass}
                                                style={{
                                                    maxHeight: "30px",
                                                    maxWidth: "30px"
                                                }}
                                                alt=""
                                            />
                                            &nbsp;
                                            <small>{workingPass.split("/").pop()}</small>
                                            <small
                                                style={{
                                                    marginLeft: "20px",
                                                    textDecoration: "underline",
                                                    color: "#962013",
                                                    cursor: "pointer",
                                                }}
                                                onClick={async () => {
                                                    const decision = await modal.fire({
                                                        title: "Delete File",
                                                        text: "Are you sure to delete this file?",
                                                        showCancelButton: true,
                                                        confirmButtonText: `Delete`,
                                                    });
                                                    if (decision.isConfirmed) {
                                                        setValue("workingPass", "");
                                                    }
                                                }}
                                            >
                                                Delete
                                            </small>
                                        </p>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <TextField
                                        htmlFor={`raised-button-file-personal-photo`}
                                        label="Upload Personal Photo"
                                        className={classes.fileInput}
                                        value=""
                                        variant="outlined"
                                        placeholder="Upload Personal Photo"
                                        fullWidth
                                        InputProps={{
                                            classes: {
                                                input: classes["&::placeholder"],
                                            },
                                            endAdornment: (
                                                <Controller
                                                    control={control}
                                                    name="personalPhoto"
                                                    render={({ field }) => {
                                                        return (
                                                            <InputAdornment
                                                                position="end">
                                                                <input
                                                                    style={{ display: "none" }}
                                                                    id={`raised-button-file-personal-photo`}
                                                                    type="file"
                                                                    onChange={(e) => {
                                                                        handleFileUpload("personalPhoto", e);
                                                                    }}
                                                                />
                                                                <Button
                                                                    htmlFor={`raised-button-file-personal-photo`}
                                                                    variant="outlined"
                                                                    component="label"
                                                                    size="small"
                                                                >
                                                                    Choose
                                                                </Button>
                                                            </InputAdornment>
                                                        );
                                                    }}
                                                />
                                            ),
                                        }}
                                    />

                                    {personalPhoto && (
                                        <p>
                                            {checkExtIsImg(personalPhoto) && (
                                                <>
                                                    <img
                                                        src={personalPhoto}
                                                        style={{
                                                            maxHeight: "30px",
                                                            maxWidth: "30px"
                                                        }}
                                                        onClick={() => window.open(personalPhoto, '_blank').focus()}
                                                        alt=""
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <A href={personalPhoto}
                                                target="_blank"><small>{personalPhoto.split("/").pop()}</small></A>
                                            <small
                                                style={{
                                                    marginLeft: "20px",
                                                    textDecoration: "underline",
                                                    color: "#962013",
                                                    cursor: "pointer",
                                                }}
                                                onClick={async () => {
                                                    const decision = await modal.fire({
                                                        title: "Delete Image",
                                                        text: "Are you sure to delete this file?",
                                                        showCancelButton: true,
                                                        confirmButtonText: `Delete`,
                                                    });
                                                    if (decision.isConfirmed) {
                                                        setValue("personalPhoto", "");
                                                    }
                                                }}
                                            >
                                                Delete
                                            </small>
                                        </p>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}
                                spacing={3}>
                                <Grid item xs={6}>
                                    <TextField
                                        htmlFor={`raised-button-file-business-card`}
                                        label="Upload Business Card"
                                        className={classes.fileInput}
                                        value=""
                                        variant="outlined"
                                        placeholder="Upload Business Card"
                                        fullWidth
                                        InputProps={{
                                            classes: {
                                                input: classes["&::placeholder"],
                                            },
                                            endAdornment: (
                                                <Controller
                                                    control={control}
                                                    name="businessCard"
                                                    render={({ field }) => {
                                                        return (
                                                            <InputAdornment
                                                                position="end">
                                                                <input
                                                                    style={{ display: "none" }}
                                                                    id={`raised-button-file-business-card`}
                                                                    type="file"
                                                                    onChange={(e) => {
                                                                        handleFileUpload("businessCard", e);
                                                                    }}
                                                                />
                                                                <Button
                                                                    htmlFor={`raised-button-file-business-card`}
                                                                    variant="outlined"
                                                                    component="label"
                                                                    size="small"
                                                                >
                                                                    Choose
                                                                </Button>
                                                            </InputAdornment>
                                                        );
                                                    }}
                                                />
                                            ),
                                        }}
                                    />

                                    {businessCard && (
                                        <p>
                                            {checkExtIsImg(businessCard) && (
                                                <>
                                                    <img
                                                        src={businessCard}
                                                        style={{
                                                            maxHeight: "30px",
                                                            maxWidth: "30px"
                                                        }}
                                                        onClick={() => window.open(businessCard, '_blank').focus()}
                                                        alt=""
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <A href={businessCard}
                                                target="_blank"><small>{businessCard.split("/").pop()}</small></A>
                                            <small
                                                style={{
                                                    marginLeft: "20px",
                                                    textDecoration: "underline",
                                                    color: "#962013",
                                                    cursor: "pointer",
                                                }}
                                                onClick={async () => {
                                                    const decision = await modal.fire({
                                                        title: "Delete Image",
                                                        text: "Are you sure to delete this file?",
                                                        showCancelButton: true,
                                                        confirmButtonText: `Delete`,
                                                    });
                                                    if (decision.isConfirmed) {
                                                        setValue("personalPhoto", "");
                                                    }
                                                }}
                                            >
                                                Delete
                                            </small>
                                        </p>
                                    )}
                                </Grid>
                            </Grid>

                            {[0, 1].map(i => {
                                return (
                                    <Grid container item
                                        xs={12}
                                        spacing={3}>
                                        <Grid item xs={6}>
                                            <TextField
                                                htmlFor={`raised-button-file-newsclippingspath-${i}`}
                                                label={`Upload News Clippings ${i + 1}`}
                                                className={classes.fileInput}
                                                value=""
                                                variant="outlined"
                                                placeholder={`Upload News Clippings ${i + 1}`}
                                                fullWidth
                                                InputProps={{
                                                    classes: {
                                                        input: classes["&::placeholder"],
                                                    },
                                                    endAdornment: (
                                                        <Controller
                                                            control={control}
                                                            name={`newsClippingsPath.${i}`}
                                                            render={({ field }) => {
                                                                return (
                                                                    <InputAdornment
                                                                        position="end">
                                                                        <input
                                                                            style={{ display: "none" }}
                                                                            id={`raised-button-file-newsclippingspath-${i}`}
                                                                            type="file"
                                                                            onChange={(e) => {
                                                                                handleFileUpload("newsClippingsPath", e);
                                                                            }}
                                                                        />
                                                                        <Button
                                                                            htmlFor={`raised-button-file-newsclippingspath-${i}`}
                                                                            variant="outlined"
                                                                            component="label"
                                                                            size="small"
                                                                        >
                                                                            Choose
                                                                        </Button>
                                                                    </InputAdornment>
                                                                );
                                                            }}
                                                        />
                                                    ),
                                                }}
                                            />

                                            {newsClippingsPath[i] && (
                                                <p>
                                                    {checkExtIsImg(newsClippingsPath[i]) && (
                                                        <>
                                                            <img
                                                                src={newsClippingsPath[i]}
                                                                style={{
                                                                    maxHeight: "30px",
                                                                    maxWidth: "30px"
                                                                }}
                                                                onClick={() => window.open(newsClippingsPath[i], '_blank').focus()}
                                                                alt=""
                                                            />
                                                            &nbsp;
                                                        </>
                                                    )}
                                                    <A href={newsClippingsPath[i]}
                                                        target="_blank"><small>{newsClippingsPath[i].split("/").pop()}</small></A>
                                                    <small
                                                        style={{
                                                            marginLeft: "20px",
                                                            textDecoration: "underline",
                                                            color: "#962013",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={async () => {
                                                            const decision = await modal.fire({
                                                                title: "Delete Image",
                                                                text: "Are you sure to delete this file?",
                                                                showCancelButton: true,
                                                                confirmButtonText: `Delete`,
                                                            });
                                                            if (decision.isConfirmed) {
                                                                setValue(`newsClippingsPath.${i}`, "");
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </small>
                                                </p>
                                            )}
                                        </Grid>
                                    </Grid>
                                )
                            })}

                        </Box>

                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>News
                                        Language</strong>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        {Object.keys(defaultValue.newsLang).map((key) => (
                                            <Controller
                                                name={`newsLang.${key}`}
                                                control={control}
                                                defaultValue={defaultValue.newsLang[key]}
                                                render={({
                                                    field: {
                                                        value,
                                                        onChange,
                                                        ...rest
                                                    },
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <FormControlLabel
                                                            control={
                                                                <CustomCheckbox
                                                                    color="primary"
                                                                    checked={value}
                                                                    onChange={(event) => {
                                                                        onChange(event.target.checked);
                                                                        errors.newsLang && delete errors.newsLang;
                                                                    }}
                                                                    className={classes.checkbox}
                                                                    {...rest}
                                                                    style={errors.newsLang && { color: "red" }}
                                                                />
                                                            }
                                                            className={classes.formLabel}
                                                            label={key}
                                                        />
                                                    )
                                                }
                                                }
                                            />
                                        ))}
                                        <FormHelperText
                                            className={classes.errors}>
                                            {
                                                errors.newsLang && errors.newsLang.message
                                            }
                                        </FormHelperText>
                                    </FormGroup>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                direction="row"
                                spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Duration</strong>
                                </Grid>
                                <Grid container item xs={6}
                                    direction="row">
                                    <Grid item xs={6}>
                                        <Controller
                                            name={`durationFrom`}
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <KeyboardDatePicker
                                                    fullWidth
                                                    variant="inline"
                                                    format="dd/MM/yyyy"
                                                    margin="dense"
                                                    id="date-picker-inline"
                                                    label="From"
                                                    {...field}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    classes={{ root: classes.customLabel }}
                                                    KeyboardButtonProps={{
                                                        "aria-label": "change date",
                                                    }}
                                                    helperText={""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Controller
                                            name={`durationTo`}
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <KeyboardDatePicker
                                                    fullWidth
                                                    variant="inline"
                                                    format="dd/MM/yyyy"
                                                    margin="dense"
                                                    id="date-picker-inline"
                                                    label="To"
                                                    {...field}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    classes={{ root: classes.customLabel }}
                                                    KeyboardButtonProps={{
                                                        "aria-label": "change date",
                                                    }}
                                                    helperText={""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item xs={12}
                                    spacing={3}>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="contactAddress"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    label="Contact Address"
                                                    variant="outlined"
                                                    margin="dense"
                                                    className={classes.textInput}
                                                    fullWidth
                                                    {...field}
                                                    error={!!error}
                                                    helperText={error ? error.message : ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item xs={12}
                                    spacing={3}>
                                    <Grid item xs={6}>
                                        <Controller
                                            name="contactTelephone"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    label="Contact Telephone"
                                                    variant="outlined"
                                                    margin="dense"
                                                    className={classes.textInput}
                                                    fullWidth
                                                    {...field}
                                                    error={!!error}
                                                    helperText={error ? error.message : ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Controller
                                            name="contactFax"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    label="Contact Fax"
                                                    variant="outlined"
                                                    margin="dense"
                                                    className={classes.textInput}
                                                    fullWidth
                                                    {...field}
                                                    error={!!error}
                                                    helperText={error ? error.message : ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item xs={12}
                                    direction="row">
                                    <Grid container item
                                        direction="column"
                                        spacing={1}
                                        xs={12}>
                                        <Grid item>
                                            <Typography
                                                className={classes.itemTitle}>
                                                HKIFF Status
                                            </Typography>
                                        </Grid>
                                        <Grid container item
                                            xs={12}
                                            spacing={3}>
                                            <Grid item
                                                xs={6}>
                                                <Controller
                                                    control={control}
                                                    name="status"
                                                    render={({
                                                        field: {
                                                            value,
                                                            onChange,
                                                            ...rest
                                                        },
                                                        fieldState: { error },
                                                    }) => (
                                                        <ToggleButtonGroup
                                                            {...rest}
                                                            value={value}
                                                            onChange={(event, newFormats) => {
                                                                if (newFormats !== null) {
                                                                    onChange(newFormats);
                                                                    setEmailType(newFormats === 1 ? 'm_a_confirm' : newFormats === 2 ? 'm_a_reject' : '');
                                                                }
                                                            }}
                                                            exclusive
                                                            aria-label="Status"
                                                            className={classes.input}
                                                        >
                                                            <ToggleButton
                                                                value={1}
                                                                aria-label="Approved"
                                                                style={{ width: "33%" }}
                                                                disabled={HkiffDisabled}
                                                            >
                                                                Approved
                                                            </ToggleButton>
                                                            <ToggleButton
                                                                value={3}
                                                                aria-label="TBC"
                                                                style={{ width: "33%" }}
                                                                disabled={HkiffDisabled}
                                                            >
                                                                TBC
                                                            </ToggleButton>
                                                            <ToggleButton
                                                                value={2}
                                                                aria-label="Rejected"
                                                                style={{ width: "33%" }}
                                                                disabled={HkiffDisabled}
                                                            >
                                                                Rejected
                                                            </ToggleButton>
                                                        </ToggleButtonGroup>
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item
                                                xs={2}>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={disabled || !emailType}
                                                    onClick={() => sendMediaEmail(emailType)}
                                                    style={{
                                                        marginRight: "5px",
                                                        padding: "12px 16px",
                                                        borderColor: "#306F9D",
                                                        width: "100%",
                                                        margin: 0,
                                                    }}
                                                >
                                                    Send Email
                                                </Button>
                                            </Grid>
                                            <Grid item
                                                xs={2}>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={disabled || watch('status') !== 1 || !watch('passcode')}
                                                    onClick={sendPasscodeEmail}
                                                    style={{
                                                        marginRight: "5px",
                                                        padding: "12px 16px",
                                                        borderColor: "#306F9D",
                                                        width: "100%",
                                                        margin: 0,
                                                    }}
                                                >
                                                    Send Passcode Email
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container item xs={12}
                                    direction="row">
                                    <Grid container item
                                        direction="column"
                                        spacing={1}
                                        xs={12}>
                                        <Grid item>
                                            <Typography
                                                className={classes.itemTitle}>
                                                HAF Status
                                            </Typography>
                                        </Grid>
                                        <Grid container item
                                            xs={12}
                                            spacing={3}>
                                            <Grid item
                                                xs={4}>
                                                <Controller
                                                    control={control}
                                                    name="hafStatus"
                                                    render={({
                                                        field: {
                                                            value,
                                                            onChange,
                                                            ...rest
                                                        },
                                                        fieldState: { error },
                                                    }) => (
                                                        <ToggleButtonGroup
                                                            {...rest}
                                                            value={value}
                                                            onChange={(event, newFormats) => {
                                                                if (newFormats !== null) {
                                                                    onChange(newFormats);
                                                                    setHafType(newFormats === 1 ? 'm_a_haf_confirm' : newFormats === 2 ? 'm_a_haf_reject' : '');
                                                                }
                                                            }}
                                                            exclusive
                                                            aria-label="Haf Status"
                                                            className={classes.input}
                                                        >
                                                            <ToggleButton
                                                                value={1}
                                                                aria-label="Approved"
                                                                style={{ width: "33%" }}
                                                                disabled={hafDisabled}
                                                            >
                                                                Approved
                                                            </ToggleButton>
                                                            <ToggleButton
                                                                value={3}
                                                                aria-label="TBC"
                                                                style={{ width: "33%" }}
                                                                disabled={hafDisabled}
                                                            >
                                                                TBC
                                                            </ToggleButton>
                                                            <ToggleButton
                                                                value={2}
                                                                aria-label="Rejected"
                                                                style={{ width: "33%" }}
                                                                disabled={hafDisabled}
                                                            >
                                                                Rejected
                                                            </ToggleButton>
                                                        </ToggleButtonGroup>
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item
                                                xs={2}>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={disabled || !hafType}
                                                    onClick={() => sendMediaEmail(hafType)}
                                                    style={{
                                                        marginRight: "5px",
                                                        padding: "12px 16px",
                                                        borderColor: "#306F9D",
                                                        width: "100%",
                                                        margin: 0,
                                                    }}
                                                >
                                                    Send
                                                    Email
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    {disabled || (
                        <div
                            className={classes.buttonGroup}>
                            <Button
                                type="submit"
                                variant="contained"
                                disableElevation
                                style={{
                                    color: "white",
                                    background: "#306F9D",
                                    padding: "9px 16px",
                                }}
                            >
                                Save Changes
                            </Button>
                        </div>
                    )}
                </fieldset>
            </form>
        </Box>
    );
}

export default MediaAccreditationDetailPage;
